import { useCallback, useState } from "react";

import { useGetZipDocumentWorkflowQuery } from "src/graphql/generated/queries";
import { type DocumentFragment } from "src/graphql/generated/types";
import { useSnackbar } from "src/hooks/useSnackbar";
import { getSpecificError } from "src/utils";

import { POLLING_INTERVAL } from "../constants";
import { useOpenDocument } from "./useOpenDocument";

export function useZipSelectionDownload(
  filename: string
): {
  selection: Array<DocumentFragment>;
  onSelectionChange: (files: Array<DocumentFragment>) => void;
  downloadSelection: () => void;
  isLoading: boolean;
} {
  const initialRequestParams = {
    filename,
    ids: [],
  };
  const [requestParams, setRequestParams] = useState<{
    filename: string;
    ids: Array<string>;
    workflow?: string;
  }>(initialRequestParams);
  const enabled = requestParams.ids.length > 0;
  const { openDocument } = useOpenDocument();
  const { enqueueSnackbar } = useSnackbar();
  const [selection, setSelection] = useState<Array<DocumentFragment>>([]);

  const { data, error } = useGetZipDocumentWorkflowQuery(requestParams, {
    refetchInterval: POLLING_INTERVAL,
    enabled,
  });

  if (
    data?.zipFileWorkflow.__typename === "ZipFileWorkflowAcceptedResult" &&
    !requestParams.workflow
  ) {
    setRequestParams({
      ...requestParams,
      workflow: data.zipFileWorkflow.workflow,
    });
  }

  if (data?.zipFileWorkflow.__typename === "ZipFileWorkflowResult") {
    setRequestParams(initialRequestParams);

    window.open(data.zipFileWorkflow.url, "_blank");
  }

  if (error) {
    setRequestParams(initialRequestParams);

    enqueueSnackbar(
      getSpecificError(error, "Une erreur est survenue lors du téléchargement des documents"),
      {
        variant: "error",
      }
    );
  }

  const setDownloadDocuments = useCallback(
    (files: Array<DocumentFragment>) => setSelection(files),
    []
  );

  const downloadSelection = useCallback(() => {
    if (selection.length === 1) {
      openDocument(selection[0].hashFile);
    } else {
      setRequestParams({
        ...initialRequestParams,
        ids: selection.map((document) => document.id),
      });
    }
  }, [selection]);

  return {
    selection,
    onSelectionChange: setDownloadDocuments,
    downloadSelection,
    isLoading: enabled,
  };
}
